import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * NSherjFh PR 12 — diary photo hard-delete confirm modal.
 *
 * Second HARD-delete modal in the umbrella (after PR 11 notes).
 * Same modal shell as PRs 2-10 soft-delete modals + PR 11 notes
 * hard-delete: AlertTriangle icon, destructive warning callout,
 * outline Cancel + destructive Confirm.
 *
 * Two distinctions from PR 11:
 *   1. Storage cleanup is explicit in the copy — "the file is
 *      removed from storage" — so admins/SMs understand this isn't
 *      just a row-level deletion.
 *   2. The body callout names the policy reason for hard-delete
 *      (storage hygiene per Heidi's 28 May call) to set the right
 *      expectation: no Recycle Bin, no restore, no "undo".
 */
const PhotoDeleteConfirmModal = ({ isOpen, onClose, onConfirm, isDeleting = false }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-md">
        <div className="p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-destructive/10 rounded-full flex-shrink-0">
              <Icon name="AlertTriangle" size={24} color="var(--color-destructive)" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-foreground mb-2">
                Delete this photo?
              </h2>
              <p className="text-sm text-muted-foreground">
                This will permanently delete the photo. This cannot be undone.
              </p>
            </div>
          </div>

          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-2">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">No recovery</p>
                <p className="text-sm text-muted-foreground">
                  Photos are not recoverable after deletion — the file is removed from storage. Unlike diary entries, documents, RAMS, and other records (restorable from a Deleted tab), there is no recycle bin for photos.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
            iconName={isDeleting ? 'Loader2' : 'Trash2'}
            iconPosition="left"
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PhotoDeleteConfirmModal;
